import { FC, useState, useEffect } from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import theme from "@/theme";
import MessageSvg from "@/assets/svg/MessageSvg";
import HeartSvg from "@/assets/svg/HeartSvg";
import ShareSvg from "@/assets/svg/ShareSvg";
import LightningSvg from "@/assets/svg/LightningSvg";
import SavedSvg from "@/assets/svg/SavedSvg";
import { IPost } from "@/store/api/postApiData";
import { useGetWalletBalanceQuery } from "@/store/api/walletApi";
import { useSavePostMutation, useSavePostDeleteMutation } from "@/store/api/postApi";
import { useNavigate } from "react-router-dom";
import { useLikeMutation } from "@/store/api/walletApi";
import LikeUndoModal from "@/components/UI/modal/Wallet/LikeZap/LikeUndoModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { styles } from "./stylesPostActions";
import SendZapModal from "@/components/UI/modal/Wallet/LikeZap/SendZapModal";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { RequireAuth } from "@/components/RequireAuth";
import { useGetUserSettingsQuery } from "@/store/api/authApi";

interface IPostActionsProps {
  post: IPost;
  onDelete?: (uid: string) => void;
  zapsCountState: number;
  setZapsCountState: React.Dispatch<React.SetStateAction<number>>;
  isLikedTemporary: boolean;
  setIsLikedTemporary: React.Dispatch<React.SetStateAction<boolean>>;
  setTotalZapsAmountState: React.Dispatch<React.SetStateAction<number>>;
  totalZapsAmountState: number;
  onZapSuccess?: (userAvatar: string) => void;
  onLikeSuccess?: () => void;
}

const PostActions: FC<IPostActionsProps> = ({
  post,
  onDelete,
  zapsCountState,
  setZapsCountState,
  isLikedTemporary,
  setIsLikedTemporary,
  setTotalZapsAmountState,
  totalZapsAmountState,
  onZapSuccess,
  onLikeSuccess,
}) => {
  const [savePost] = useSavePostMutation();
  const [savePostDelete] = useSavePostDeleteMutation();
  const [isSavedState, setIsSavedState] = useState(post.isSaved);
  const [copied, setCopied] = useState(false);
  const [likeWallet] = useLikeMutation();
  const navigate = useNavigate();
  const { data: balanceData, refetch: refetchBalance, isLoading } = useGetWalletBalanceQuery();
  const [showLikeModal, setShowLikeModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);
  const [hasJustLiked, setHasJustLiked] = useState(false);
  const isActuallyLiked = post.isLiked || hasJustLiked;
  const [showZapModal, setShowZapModal] = useState(false);
  const userId = useSelector((state: RootState) => state.auth.user?.uid);
  const [isLiking, setIsLiking] = useState(false);
  const isOwnPost = post.author?.uid === userId;

  const { data: userSettings } = useGetUserSettingsQuery();
  const likePrice = userSettings?.customLikeAmountUsdt ?? 0.25;

  const handleZapSuccess = (avatarUrl?: string) => {
    setZapsCountState((prev) => prev + 1);
    onZapSuccess?.(avatarUrl ?? "");
  };
  useEffect(() => {
    setIsSavedState(post.isSaved);
  }, [post.isSaved]);

  const clickLike = () => {
    if (!userId || isLiking || post.isLiked) {
      return;
    } else {
      const balance = balanceData?.balance ?? 0;
      const hasEnoughBalance = balance >= likePrice;
      if (isOwnPost) {
        setToast({ message: "You can't like your own post", success: false });
        return;
      }
      if (!hasEnoughBalance) {
        setToast({ message: "There are not enough USDC in your account.", success: false });
        return;
      }

      if (!post.isLiked) {
        setShowLikeModal(true);
      }
    }
  };
  const handleClose = async () => {
    setShowLikeModal(false);
    setIsLiking(true);
    try {
      await likeWallet({ post_id: post.uid }).unwrap();
      refetchBalance();
      setHasJustLiked(true);
      setIsLikedTemporary(true);
      onLikeSuccess?.();
    } catch (e: any) {
      const msg = e?.data?.message || e?.error || "Something went wrong";
      if (e?.status === 400 && typeof msg === "string") {
        setToast({ message: msg, success: false });
        setHasJustLiked(false);
        setIsLikedTemporary(false);
      } else {
        setToast({ message: "Something went wrong", success: false });
        setIsLikedTemporary(false);
        setHasJustLiked(false);
      }
    } finally {
      setIsLiking(false);
    }
  };

  //zap
  const clickLightning = () => {
    const balance = balanceData?.balance ?? 0;
    if (!isActuallyLiked || isLoading) return;
    if (balance < 1) {
      setToast({ message: "There are not enough USDC in your account.", success: false });
      return;
    }

    setShowZapModal(true);
  };

  const clickShare = () => {
    // const postUrl = `${window.location.origin}/post/${post.uid}`;
    const postUrl = `https://develop.wethemarkt.com/post/${post.uid}`;
    navigator.clipboard.writeText(postUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const clickSaved = async () => {
    const newSavedState = !isSavedState;
    setIsSavedState(newSavedState);

    if (!newSavedState && onDelete) {
      onDelete(post.uid);
    }

    try {
      if (isSavedState) {
        await savePostDelete(post.uid).unwrap();
      } else {
        await savePost(post.uid).unwrap();
      }
    } catch (e: any) {
      console.error("Failed to toggle saved post", e);
      setIsSavedState((prev) => !prev);
      if (isSavedState) {
        setToast({ message: "Failed to delete post from saved posts", success: false });
      } else {
        setToast({ message: "Failed to save post", success: false });
      }
    }
  };
  const displayedLikes = post.likesCount + (isLikedTemporary && !post.isLiked ? 1 : 0);
  return (
    <Stack
      direction="row"
      justifyContent={"space-between"}
      alignItems={"center"}
      sx={{ marginTop: "1rem", minHeight: "2.875rem", width: "100%" }}
    >
      {showZapModal && post.author && (
        <SendZapModal
          open={showZapModal}
          onClose={() => setShowZapModal(false)}
          username={post.author.username}
          avatarUrl={post.author.image?.url || ""}
          postUid={post.uid}
          onSuccess={handleZapSuccess}
          refetchBalance={refetchBalance}
          setTotalZapsAmountState={setTotalZapsAmountState}
          totalZapsAmountState={totalZapsAmountState}
          setToast={setToast}
        />
      )}
      {showLikeModal && (
        <LikeUndoModal
          username={post.author?.username ?? "unknown"}
          amount={String(likePrice)}
          onCancel={() => {
            setIsLikedTemporary(false);
            setShowLikeModal(false);
          }}
          onClose={handleClose}
        />
      )}
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <RequireAuth>
        <Button
          onClick={() => navigate(`/post/${post.uid}`)}
          sx={{
            padding: "0.25rem",
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            borderRadius: "1.25rem",
            ":hover": {
              bgcolor: "#F2F7F2",
              "& .message-icon": {
                path: {
                  stroke: theme.palette.custom.secondaryGreen,
                },
              },
              "& .comment-count": {
                color: theme.palette.custom.secondaryGreen,
              },
            },
          }}
        >
          <MessageSvg className="message-icon" />
          <Typography
            color={theme.palette.custom.blueGray600}
            variant="h5"
            className="comment-count"
          >
            {post.commentsCount}
          </Typography>
        </Button>
      </RequireAuth>
      <RequireAuth>
        <Button
          onClick={clickLike}
          sx={{
            padding: "0.25rem",
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            borderRadius: "1.25rem",
            "& .like-icon": {
              path: {
                fill: post.isLiked || isLikedTemporary || hasJustLiked ? "#FF403D" : "none",
                stroke:
                  post.isLiked || isLikedTemporary || hasJustLiked
                    ? "#FF403D"
                    : theme.palette.custom.blueGray600,
              },
            },
            ":hover": {
              bgcolor: "#FFF2F2",
              "& .like-icon": {
                path: {
                  stroke: "#FF403D",
                },
              },
              "& .comment-count": {
                color: theme.palette.custom.secondaryGreen,
              },
            },
          }}
        >
          <HeartSvg className="like-icon" path="none" />
          <Typography color={theme.palette.custom.blueGray600} variant="h5">
            {displayedLikes}
          </Typography>
        </Button>
      </RequireAuth>
      <Tooltip
        title={
          !isActuallyLiked ? (
            <Box sx={styles.TooltipWrapper}>
              <Typography sx={styles.TooltipText}>
                You can send a Zap only after you like a post.
              </Typography>
            </Box>
          ) : (
            ""
          )
        }
        placement="top"
        disableHoverListener={isActuallyLiked}
        componentsProps={{
          tooltip: {
            sx: styles.TooltipProps,
          },
        }}
      >
        <span>
          <RequireAuth>
            <Button
              onClick={clickLightning}
              disabled={!isActuallyLiked || isLoading}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                borderRadius: "1.25rem",
                cursor: !isActuallyLiked ? "not-allowed" : "pointer",
                "& .lightning-icon": {
                  path: {
                    fill: isActuallyLiked ? "#FFAD54" : "none",
                    stroke: isActuallyLiked ? "#FFAD54" : theme.palette.custom.blueGray600,
                  },
                },
                ":hover": {
                  bgcolor: isActuallyLiked ? "#FFF2F2" : "transparent",
                  "& .lightning-icon": {
                    path: {
                      stroke: isActuallyLiked ? "#FFAD54" : theme.palette.custom.blueGray600,
                    },
                  },
                  "& .lightning-count": {
                    color: isActuallyLiked ? "#FFAD54" : theme.palette.custom.blueGray600,
                  },
                },
              }}
            >
              <LightningSvg className="lightning-icon" />
              <Typography
                className="lightning-count"
                color={theme.palette.custom.blueGray600}
                variant="h5"
              >
                {zapsCountState}
              </Typography>
            </Button>
          </RequireAuth>
        </span>
      </Tooltip>

      <Stack direction="row" gap={"0.25rem"} alignItems={"center"}>
        <RequireAuth>
          <Tooltip
            title={copied ? "Copied!" : "Share?"}
            placement="top"
            componentsProps={{
              tooltip: {
                sx: styles.TooltipProps,
              },
            }}
          >
            <Button
              onClick={clickShare}
              sx={{
                padding: "0",
                margin: "0",
                minWidth: "2rem",
                minHeight: "2rem",
                borderRadius: "50%",
                ":hover": {
                  bgcolor: "#F2F7F2",
                  "& .share-icon": {
                    path: {
                      stroke: theme.palette.custom.blueGray900,
                    },
                  },
                },
              }}
            >
              <ShareSvg className="share-icon" />
            </Button>
          </Tooltip>
        </RequireAuth>
      </Stack>
      <Stack direction="row" gap={"0.25rem"} alignItems={"center"}>
        <RequireAuth>
          <Button
            onClick={clickSaved}
            sx={{
              padding: "0",
              margin: "0",
              minWidth: "2rem",
              minHeight: "2rem",
              borderRadius: "50%",
              "& .saved-icon": {
                path: {
                  fill: isSavedState ? theme.palette.custom.mainBlue : "none",
                  stroke: isSavedState
                    ? theme.palette.custom.mainBlue
                    : theme.palette.custom.blueGray600,
                },
              },
              ":hover": {
                bgcolor: "#E3F2FF",
                "& .saved-icon": {
                  path: {
                    stroke: theme.palette.custom.mainBlue,
                  },
                },
              },
            }}
          >
            <SavedSvg className="saved-icon" />
          </Button>
        </RequireAuth>
      </Stack>
    </Stack>
  );
};

export default PostActions;
